import axios from "axios";

const TOKEN_KEY = "chintamat:token";

/** Local FastAPI default when REACT_APP_BACKEND_URL is unset (see frontend/.env.local). */
export const DEV_BACKEND_ROOT = "http://127.0.0.1:8001";

/** Strip trailing slashes and a trailing /api so env can be root or /api base. */
export function normalizeBackendRoot(raw) {
  const u = (raw || "").trim().replace(/\/+$/, "");
  if (!u) return "";
  return u.replace(/\/api$/i, "");
}

function getRuntimeBackendRoot() {
  if (typeof window === "undefined") return "";
  const url = window.__CHINTAMAT_CONFIG__?.backendUrl;
  return normalizeBackendRoot(url || "");
}

function inferProductionApiBase() {
  if (typeof window === "undefined") return "";
  const { hostname, protocol } = window.location;
  if (hostname === "api.chintamat.com") return `${protocol}//api.chintamat.com/api`;
  if (hostname.endsWith("chintamat.com")) {
    const fromConfig = getRuntimeBackendRoot();
    if (fromConfig) return `${fromConfig}/api`;
    return `${protocol}//api.chintamat.com/api`;
  }
  return "";
}

function resolveApiBase() {
  if (process.env.NODE_ENV === "development") {
    const root = normalizeBackendRoot(process.env.REACT_APP_BACKEND_URL) || DEV_BACKEND_ROOT;
    return `${root}/api`;
  }

  const root =
    normalizeBackendRoot(process.env.REACT_APP_BACKEND_URL) ||
    getRuntimeBackendRoot() ||
    inferProductionApiBase();
  if (root) return `${root}/api`;

  return "";
}

export const API_BASE = resolveApiBase();
export const BACKEND_ROOT = API_BASE.replace(/\/api$/i, "");

export const getToken = () => (typeof window === "undefined" ? null : window.localStorage.getItem(TOKEN_KEY));
export const setToken = (t) => {
  if (typeof window === "undefined") return;
  if (t) window.localStorage.setItem(TOKEN_KEY, t);
  else window.localStorage.removeItem(TOKEN_KEY);
};

export const assertApiConfigured = () => {
  if (process.env.NODE_ENV === "development") {
    return;
  }
  if (!API_BASE) {
    throw new Error(
      "API URL is not configured. Set REACT_APP_BACKEND_URL to your Render API root in Cloudflare Pages, then rebuild. See docs/DEPLOY.md.",
    );
  }
  if (typeof window !== "undefined" && API_BASE.startsWith(window.location.origin)) {
    throw new Error(
      "REACT_APP_BACKEND_URL points at this website. Set it to your Render API URL (not the Cloudflare Pages URL), then rebuild.",
    );
  }
};

/** Evidence uploads and report submit (large payloads, Render cold start). */
export const UPLOAD_TIMEOUT_MS = 180000;
/** JSON-only report submit when files were already uploaded. */
export const REPORT_TIMEOUT_MS = 120000;

const LONG_RUNNING_PATHS = ["/uploads", "/reports"];

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 90000,
  // Auth uses Bearer header in localStorage, not cookies — avoids strict CORS credential rules.
  withCredentials: false,
});

api.interceptors.request.use((config) => {
  assertApiConfigured();
  const t = getToken();
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  const path = (config.url || "").split("?")[0];
  if (LONG_RUNNING_PATHS.some((p) => path === p || path.endsWith(p))) {
    config.timeout = config.timeout ?? UPLOAD_TIMEOUT_MS;
  }
  return config;
});

export default api;
